/**
 * 状態変更 → 再計算マッピング（純関数）。
 *
 * spec §6.1「変更伝播」の実装。前後の `AppState` を比較し、必要な再計算の
 * 種類を `InvalidationFlags` として返す。本モジュールは DOM / Canvas / rAF /
 * d3-delaunay 等の副作用を一切含まない pure。
 *
 * `store.subscribe(listener)` の中で `computeInvalidation(next, prev)` を
 * 呼び、フラグに応じて render-agent / 上位 wiring が再構築・再描画を実行する。
 *
 * 設計参照:
 * - spec §6.1（変更伝播テーブル）
 * - Q2 確定: `progress` 変更時は `sceneCache` を再計算しない（rerender のみ）
 */

import type { AppState } from './types';

/**
 * 再計算フラグ。`true` のものだけを listener が実行する。
 *
 * 依存階層（上流が立つと下流も立つ。逆は立たない）:
 * ```
 * fonts ─────────────────────────────────┐
 * layout ──┬─ sceneShape ──┐             │
 *          ├─ sceneVoronoi ┤             │
 *          └─ cellMotion ──┴── (sceneCache 再構築) ─┐
 *                                                    ├─► rerender
 * textLayer ──────────────────────────────┘
 * ```
 *
 * `progress` 単独変更 → rerender のみ（spec §6.1 / Q2）。
 * `sceneCache` 再計算は `sceneShape | sceneVoronoi | cellMotion | layout` の
 * いずれかが立ったときのみ。
 */
export interface InvalidationFlags {
  /**
   * Layout 全体（outer/inner サイズ、pad）の再計算が必要。
   * 立てるトリガ: `aspectRatio` / `gutterPct` / `bleedMult` / `textMarginPct` /
   * `fontSizePct`（fontSize は textBand → pad に波及するため）。
   *
   * Layout が変わると `sceneCache` も実質無効になるため、render-agent は
   * 本フラグ単独でも scene を再構築すべき。
   */
  layout: boolean;

  /**
   * innerPoly（正多角形 + jitter）の再生成が必要。
   * 立てるトリガ: `vertexCount` / `shapeRandomness` / `shapeScale` / `seed`
   * （spec §6.1 + Phase C さらなる修正ラウンドで `shapeScale` を追加）。
   * これが立つと Voronoi も追従して再構築される（innerPoly が変わるため）。
   */
  sceneShape: boolean;

  /**
   * Voronoi（破片分割）の再構築が必要（innerPoly は流用可）。
   * 立てるトリガ: `fragmentCount`。
   * Phase J で `randomness` / `granularity` は撤去され、セル jitter と Lloyd
   * 反復は `src/render/scene.ts` 内のハードコード値で動く。
   */
  sceneVoronoi: boolean;

  /**
   * Cell 変位ベクトル / 回転の再計算が必要（Voronoi は流用可）。
   * 立てるトリガ: `innerDispPct` / `perimMult` / `speedVariance`（spec §6.1）。
   *
   * 注: 現状の `buildScene` は Voronoi と変位を一括計算するため、render-agent は
   * 本フラグでも scene 全体を作り直して良い。将来 voronoi/cell 分離時の
   * 最適化フックとして残す。
   */
  cellMotion: boolean;

  /**
   * テキストレイヤーのみ再描画。
   * 立てるトリガ: `text` / `tracking` / `textEnabled` / `textColor` /
   * `latinFont` / `japaneseFont`（spec §6.1、Phase K2 で cornerPadding 撤去）。
   */
  textLayer: boolean;

  /**
   * フォント関連の UI / 描画更新。
   * 立てるトリガ: `latinFont` / `japaneseFont` / `userFonts`（spec §6.1）。
   * dropdown 表示更新と必要なら再描画に波及。
   */
  fonts: boolean;

  /**
   * 最終フレームの再描画が必要。
   * 上記いずれか、もしくは `progress` / `surfaceColor` / `innerColor` /
   * `textColor` / `backgroundColor` / `backgroundTransparent` /
   * `showSilhouette` / `showCells` / `showHull` / `showOverlay`
   * のいずれかが変化した場合に立つ。
   */
  rerender: boolean;
}

/** 全フラグ false の初期値（複製して返す）。 */
function emptyFlags(): InvalidationFlags {
  return {
    layout: false,
    sceneShape: false,
    sceneVoronoi: false,
    cellMotion: false,
    textLayer: false,
    fonts: false,
    rerender: false,
  };
}

/**
 * 前後 state を比較し、必要な再計算フラグを返す。
 *
 * spec §6.1 の表を完全実装:
 *
 * | 変更フィールド                                                              | 再計算 |
 * | --------------------------------------------------------------------------- | ------ |
 * | `aspectRatio` / `gutterPct` / `bleedMult` / `textMarginPct` / `fontSizePct` | layout → scene → render |
 * | `customAspect.width` / `customAspect.height`                                | `aspectRatio === 'custom'` のときのみ layout → scene → render（それ以外は副作用なし） |
 * | `vertexCount` / `shapeRandomness` / `shapeScale` / `seed`                   | scene → render |
 * | `fragmentCount`                                                             | scene（Voronoi 再構築、Phase J で randomness / granularity 撤去） |
 * | `innerDispPct` / `perimMult` / `speedVariance`                              | cell 変位のみ再計算（Voronoi は流用可） |
 * | `progress`                                                                  | render のみ |
 * | `followSilhouette`                                                          | render のみ |
 * | `breath.*`（pulseAmp / pulsePeriod）                                        | render のみ |
 * | `surfaceColor` / `innerColor` / `textColor` / `backgroundColor` / `backgroundTransparent` | render のみ |
 * | `overlays.surface` / `overlays.inner` / `overlays.background` / `overlays.foreground` | render のみ（Phase J で 4 スロット構造に拡張） |
 * | `text` / `tracking` / `latinFont` / `japaneseFont` / `textEnabled`           | text layer（Phase K2 で cornerPadding 撤去） |
 * | `userFonts`                                                                  | UI dropdown 更新、必要なら render |
 * | `exportMode`                                                                | invalidation なし（export 開始時に参照されるのみ） |
 * | `prePulseDuration` / `detonateDuration`                                     | invalidation なし（export 開始時 or DETONATE 押下時にだけ読む、Phase J） |
 *
 * 補足:
 * - 比較は `!==`（参照同一性）。文字列・数値は値比較、`userFonts` 配列・
 *   `sceneCache` オブジェクトは参照比較で十分（更新側は常に新しい配列 /
 *   Scene を渡す前提）。
 * - `isAnimating` / `sceneCache` 自身の差分は再計算をトリガしない
 *   （これらは listener が **書き戻す**結果側のフィールドであり、
 *   トリガすると無限ループになる）。
 * - Q2 確定: `progress` 単独変更では `sceneCache` を再計算しない。
 * - debug toggle（showX）変化は `rerender` のみ立てる。
 *
 * @param next - 更新後の state
 * @param prev - 更新前の state
 * @returns どの再計算が必要かを示すフラグ集合
 */
export function computeInvalidation(next: AppState, prev: AppState): InvalidationFlags {
  const flags = emptyFlags();

  // ── layout（aspectRatio + 比率群 + fontSizePct） ───────────────
  // fontSizePct も layout に含めるのは spec §3.1 で `pad` に fontSize × 0.4 が
  // 含まれており textBand = fontSize × 1.6 も layout 派生値のため。
  if (
    next.aspectRatio !== prev.aspectRatio ||
    next.gutterPct !== prev.gutterPct ||
    next.bleedMult !== prev.bleedMult ||
    next.textMarginPct !== prev.textMarginPct ||
    next.fontSizePct !== prev.fontSizePct
  ) {
    flags.layout = true;
    flags.rerender = true;
  }

  // ── customAspect（width / height） ─────────────────────────────
  // `aspectRatio === 'custom'` のときのみ layout を無効化する。
  // preset aspect 選択中は `customAspect` を編集しても canvas 寸法に影響しない
  // ため副作用なし（rerender も立てない）。object 比較は width / height の
  // shallow 値比較で十分。
  if (
    next.customAspect.width !== prev.customAspect.width ||
    next.customAspect.height !== prev.customAspect.height
  ) {
    if (next.aspectRatio === 'custom') {
      flags.layout = true;
      flags.rerender = true;
    }
  }

  // ── sceneShape（innerPoly 再生成） ─────────────────────────────
  if (
    next.vertexCount !== prev.vertexCount ||
    next.shapeRandomness !== prev.shapeRandomness ||
    next.shapeScale !== prev.shapeScale ||
    next.seed !== prev.seed
  ) {
    flags.sceneShape = true;
    flags.rerender = true;
  }

  // ── sceneVoronoi（破片分割再構築） ─────────────────────────────
  // Phase J（F）: randomness / granularity を state から撤去したため、ここで
  // 比較するのは fragmentCount のみ。
  if (next.fragmentCount !== prev.fragmentCount) {
    flags.sceneVoronoi = true;
    flags.rerender = true;
  }

  // ── cellMotion（変位ベクトル / 回転） ──────────────────────────
  if (
    next.innerDispPct !== prev.innerDispPct ||
    next.perimMult !== prev.perimMult ||
    next.speedVariance !== prev.speedVariance
  ) {
    flags.cellMotion = true;
    flags.rerender = true;
  }

  // ── textLayer（テキスト関連） ──────────────────────────────────
  // Phase K2 Step 3: `cornerPadding` は Phase C.1 Step 3 以降 text path が
  // spline 補間に変わって `corners=[]` になり drawTextAlongPath で no-op 化
  // していたため、UI/state/render から完全撤去。ここでも比較対象から除外。
  if (
    next.text !== prev.text ||
    next.tracking !== prev.tracking ||
    next.textEnabled !== prev.textEnabled ||
    next.latinFont !== prev.latinFont ||
    next.japaneseFont !== prev.japaneseFont
  ) {
    flags.textLayer = true;
    flags.rerender = true;
  }

  // ── fonts（dropdown / userFonts） ──────────────────────────────
  if (
    next.userFonts !== prev.userFonts ||
    next.latinFont !== prev.latinFont ||
    next.japaneseFont !== prev.japaneseFont
  ) {
    flags.fonts = true;
    // userFonts 単独追加は rerender に直結しないが、
    // 既存テキストの font fallback が変わる可能性があるため保守的に立てる。
    flags.rerender = true;
  }

  // ── rerender のみ（progress / colors / debug toggle / followSilhouette / breath） ──
  // breath は object のため shallow compare（参照比較では default vs 同値 object
  // の区別ができず誤検知するため、サブフィールド毎に値比較）。
  // exportMode / prePulseDuration / detonateDuration は invalidation の対象外
  // （export 開始時 or DETONATE 押下時に読むのみで、現フレーム描画には影響
  // しない、Phase J）。よって本ブロックでも他ブロックでも判定しない。
  if (
    next.progress !== prev.progress ||
    next.surfaceColor !== prev.surfaceColor ||
    next.innerColor !== prev.innerColor ||
    next.textColor !== prev.textColor ||
    next.backgroundColor !== prev.backgroundColor ||
    next.backgroundTransparent !== prev.backgroundTransparent ||
    next.overlays.surface !== prev.overlays.surface ||
    next.overlays.inner !== prev.overlays.inner ||
    next.overlays.background !== prev.overlays.background ||
    next.overlays.foreground !== prev.overlays.foreground ||
    next.followSilhouette !== prev.followSilhouette ||
    next.breath.pulseAmp !== prev.breath.pulseAmp ||
    next.breath.pulsePeriod !== prev.breath.pulsePeriod ||
    next.showSilhouette !== prev.showSilhouette ||
    next.showCells !== prev.showCells ||
    next.showHull !== prev.showHull ||
    next.showOverlay !== prev.showOverlay
  ) {
    flags.rerender = true;
  }

  return flags;
}
